import clsx from 'clsx';
import { Dispatch, SetStateAction } from 'react';
import { EventAccordionItemProps } from 'src/components/Accordions';
import {
  AverageTicketPriceDisplay,
  SalesAndMarginDisplay,
  TicketsListedAndSoldDisplay,
} from 'src/components/Listings/InventorySummaryWidgets';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogMetricsContext } from 'src/contexts/CatalogMetricsContext';
import { MultiSelectScope } from 'src/contexts/MultiSelectionContext';
import { hideMobile } from 'src/core/themes/shared.css';
import { Stack } from 'src/core/ui';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { getInventorySummaryListingMetricsDisplayStrings } from 'src/utils/ticketMetricUtils';
import { EventWithData, Listing } from 'src/WebApiController';
import { Feature, ListingMetrics } from 'src/WebApiController';

import { LastReviewedDisplay } from '../../InventorySummaryWidgets/LastReviewedDisplay';
import * as styles from '../InventoryEventPage.css';
import { useCatchUpParams } from '../InventoryEventPage.hooks';

type InventoryEventContentProps = {
  accordionItemProps?: EventWithData;
  inventoryAccordionItem?: EventAccordionItemProps<Listing>;
  selectedListingIds: number[];
  setSelectedListingIds: Dispatch<SetStateAction<number[]>>;
  cleanMode?: boolean; // where we don't want the metrics and last reviewed content before the table content
};

export function InventoryEventInfoSection({
  accordionItemProps,
  inventoryAccordionItem,
  selectedListingIds,
  setSelectedListingIds,
  cleanMode,
}: InventoryEventContentProps) {
  const event = accordionItemProps?.event;
  const { BodyComponent, AfterHeaderComponent } = inventoryAccordionItem || {};

  const isMobileLayout = useMatchMedia('mobile');
  const hasListingTableV2Feature = useUserHasFeature(Feature.ListingTableV2);
  const hasMobileInventoryDetailsViewFeature = useUserHasFeature(
    Feature.MobileInventoryEventPage
  );

  const hasEventListingTableHotkeysFeature = useUserHasFeature(
    Feature.EventListingTableHotkeys
  );
  const hasBlockInventorySummaryStatsFeature = useUserHasFeature(
    Feature.BlockInventorySummaryStats
  );

  const { loginContext } = useAppContext();
  const hasConsignorMasterUI = useUserHasConsignorMasterUI();
  const { isCatchupMode } = useCatchUpParams();

  const { eventMetrics: eventMetricsMap } =
    useCatalogMetricsContext<ListingMetrics>();

  const eventMetrics = eventMetricsMap?.[event?.virtId ?? ''];
  const formattedTotalMetrics = eventMetrics
    ? getInventorySummaryListingMetricsDisplayStrings(
        eventMetrics,
        loginContext?.user?.activeAccount?.currencyCode
      )
    : undefined;

  return (
    <div
      className={clsx(styles.tableSection, {
        [styles.mobileTiles]:
          isMobileLayout && hasMobileInventoryDetailsViewFeature,
      })}
    >
      {BodyComponent && accordionItemProps && (
        <div
          className={clsx(
            styles.tableContainer,
            styles.inventoryEventPageTableContainer
          )}
        >
          {!cleanMode && !hasConsignorMasterUI && (
            <Stack
              gap="xl"
              flexWrap="wrap"
              className={clsx(hideMobile, styles.metricsAnimationUncollapsed, {
                [styles.metricsBlockStats]:
                  hasBlockInventorySummaryStatsFeature,
              })}
            >
              {!hasBlockInventorySummaryStatsFeature && (
                <>
                  <TicketsListedAndSoldDisplay
                    formattedTotalMetrics={formattedTotalMetrics}
                  />
                  <SalesAndMarginDisplay
                    formattedTotalMetrics={formattedTotalMetrics}
                  />
                  <AverageTicketPriceDisplay
                    formattedTotalMetrics={formattedTotalMetrics}
                  />
                </>
              )}
              {!isCatchupMode && (
                <LastReviewedDisplay viagVirtId={event?.virtId} />
              )}
            </Stack>
          )}

          <div className={styles.bulkActionsStickyWrapper}>
            {AfterHeaderComponent && (
              <AfterHeaderComponent
                displayMode={
                  hasListingTableV2Feature ? 'count-only' : undefined
                }
              />
            )}
          </div>
          <div className={styles.inventoryEventDetailsBodyWrapper}>
            <BodyComponent
              event={accordionItemProps.event}
              entities={accordionItemProps.entities.listings ?? []}
              entitiesSummary={accordionItemProps.entities.listingsSummary}
              entityCount={accordionItemProps.counts.listCnt ?? 0}
              listingGroupCount={accordionItemProps.counts.listGrpCnt}
              ungroupedListingCount={accordionItemProps.counts.ungrListCnt}
              selectedIds={selectedListingIds}
              onItemSelectionsChanged={setSelectedListingIds}
              selectionMode={{ mode: MultiSelectScope.SingleItem }}
              disablePagination
              useTableNavKeys={hasEventListingTableHotkeysFeature}
              useVirtuoso={!hasListingTableV2Feature}
            />
          </div>
        </div>
      )}
    </div>
  );
}
